import { Content, List, Root } from '@radix-ui/react-tabs';
import Page from '../Shared/Layout/Page/Page';
import CustomTrigger from '~/ui/CustomTrigger/CustomTrigger';
import TournamentGeneral, {
  ITournamentCreateData,
  ITournamentUpdateProps,
} from './TournamentGeneral/TournamentGeneral';
import TournamentLeaderboard from './TournamentLeaderboard/TournamentLeaderboard';
import Heading from '../Shared/Heading/Heading';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import styles from './Tournament.module.scss';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useToastError } from '~/utils/useToastError';
import { useNavigate, useParams } from 'react-router-dom';

interface ITournamentProps {
  isEdit?: boolean;
}

const ACTIVE_TABS = {
  GENERAL: 'general',
  LEADERBOARD: 'leaderboard',
};

const Tournament = ({ isEdit = false }: ITournamentProps) => {
  const [activeTab, setActiveTab] = useState(ACTIVE_TABS.GENERAL);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toastSuccess = useToastSuccess();
  const toastError = useToastError();
  const { id } = useParams();
  const client = useQueryClient();

  const { mutateAsync: createToutnament } = useMutation(
    async (data: ITournamentUpdateProps) => {
      const response = await instance.post('admin/game-tournament', data);
      return response;
    },
    {
      onSuccess: () => {
        toastSuccess(t('tournament_successful_created'));
        client.invalidateQueries({ queryKey: ['tournaments', id] });
        navigate(`/entertainment/${id}?activeTab=tournaments`);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const { mutateAsync: updateTournament } = useMutation(
    async ({
      tournamentId,
      data,
      paginate,
    }: {
      tournamentId: number;
      data: ITournamentCreateData;
      paginate: string;
    }) => {
      const responce = await instance.put(
        `admin/game-tournament/${tournamentId}`,
        {
          tournament: data,
        }
      );
      return { responce, paginate };
    },
    {
      onSuccess: ({ paginate }) => {
        toastSuccess(t('tournament_successful_updated'));
        client.invalidateQueries(['game-tournaments', id]);
        navigate(
          `/entertainment/${id}?activeTab=tournaments${
            paginate ? `&paginate=${paginate}` : ''
          }`
        );
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  return (
    <Page
      heading={
        <Heading
          text={`${isEdit ? t('edit_tournament') : t('add_tournament')}`}
        />
      }
    >
      <Root
        onValueChange={setActiveTab}
        value={activeTab}
        className={styles.root}
      >
        <List className={styles.tabsList}>
          <CustomTrigger
            key={ACTIVE_TABS.GENERAL}
            title={t('general')}
            value={ACTIVE_TABS.GENERAL}
            activeTab={activeTab}
          />
          {isEdit && (
            <CustomTrigger
              key={ACTIVE_TABS.LEADERBOARD}
              title={t('leaderboard')}
              value={ACTIVE_TABS.LEADERBOARD}
              activeTab={activeTab}
            />
          )}
        </List>
        <div>
          <Content value={ACTIVE_TABS.GENERAL} key={ACTIVE_TABS.GENERAL}>
            <TournamentGeneral
              isEdit={isEdit}
              updateTournament={updateTournament}
              createTournament={createToutnament}
              id={Number(id)}
            />
          </Content>
          {isEdit && (
            <Content
              value={ACTIVE_TABS.LEADERBOARD}
              key={ACTIVE_TABS.LEADERBOARD}
            >
              <TournamentLeaderboard gameId={Number(id)} />
            </Content>
          )}
        </div>
      </Root>
    </Page>
  );
};

export default Tournament;
